import { useNotify } from "../../hooks/use-notify";
import { COOKIE_ACCESS_MAIL } from "../../utils/storage-constants";
import { extractDetailMessage } from "../../api/api-instanse";
import { apiTestSubTopics } from "../../utils/api-routes";

export const useQuestionBankHandler = (api) => {
  const { toggleNotify } = useNotify();

  const getEmail = () => {
    if (typeof window === "undefined") return "";
    return (
      localStorage.getItem(COOKIE_ACCESS_MAIL) ||
      localStorage.getItem("email") ||
      localStorage.getItem("user_email") ||
      localStorage.getItem("__FANAMA_USER_EMAIL__") ||
      ""
    );
  };

  // 1. "Choose from existing questions" → Choose Questions
  // GET /api/question-bank?exam=NEET&subject=Biology&page=1
  const fetchQuestionBank = async ({ exam = "NEET", subject = "Biology", page = 1 } = {}) => {
    try {
      const { data: res } = await api.get(
        `/api/question-bank?exam=${encodeURIComponent(exam)}&subject=${encodeURIComponent(subject)}&page=${page}`
      );
      return res;
    } catch (err) {
      console.error("Error fetching question bank:", err);
      return null;
    }
  };

  // 1b. "Choose question from the question bank" → Imported Unique Questions
  // GET /api/questions/imported-unique-for-all?email=...&page=1
  const fetchImportedUniqueQuestions = async ({
    testName = "",
    exam = "",
    subject = "",
    className = "",
    chapter = "",
    concept = "",
    questionType = "",
    search = "",
    level = "",
    page = 1,
    limit = 10,
    pageSize = 10,
  } = {}) => {
    try {
      const email = getEmail();
      const params = new URLSearchParams();
      if (email) params.append("email", email);
      if (testName) {
        const cleanName = testName.replace(/[–—]/g, "-").trim();
        if (cleanName && !cleanName.includes("@")) {
          params.append("test_name", cleanName);
        }
      }
      if (exam && exam !== "All" && exam !== "all") params.append("exam", exam);
      if (className && className !== "All" && className !== "all") {
        params.append("class", className);
      }
      if (chapter && chapter !== "All" && chapter !== "all") {
        params.append("chapter", chapter);
      }
      if (concept && concept !== "All Concepts" && concept !== "all" && concept !== "All") {
        params.append("concept", concept);
      }
      if (questionType && questionType !== "All Question Types" && questionType !== "all" && questionType !== "All") {
        params.append("question_type", questionType);
      }
      if (search && String(search).trim()) {
        params.append("search", String(search).trim());
      }
      if (level && level !== "All Levels" && level !== "all" && level !== "All") {
        params.append("level", level);
      }
      if (subject && subject !== "All" && subject !== "all") params.append("subject", subject);
      if (page) params.append("page", page);
      if (limit || pageSize) {
        params.append("limit", limit || pageSize);
        params.append("page_size", pageSize || limit);
      }

      if (email) {
        try {
          const { data: res } = await api.get(
            `/api/questions/imported-unique-for-all?${params.toString()}`
          );
          if (res) return res;
        } catch (allErr) {
          console.warn("Primary /api/questions/imported-unique-for-all failed, trying fallback:", allErr);
        }
      }

      // Fallback to /api/question-bank
      const fallbackParams = new URLSearchParams();
      if (email) fallbackParams.append("email", email);
      if (exam && exam !== "All" && exam !== "all") fallbackParams.append("exam", exam);
      if (subject && subject !== "All" && subject !== "all") fallbackParams.append("subject", subject);
      if (page) fallbackParams.append("page", page);
      if (limit || pageSize) {
        fallbackParams.append("limit", limit || pageSize);
        fallbackParams.append("page_size", pageSize || limit);
      }

      const { data: fbRes } = await api.get(
        `/api/question-bank?${fallbackParams.toString()}`
      );
      return fbRes;
    } catch (err) {
      console.error("Error fetching imported unique questions:", err);
      return null;
    }
  };

  // 2. "Bring your own questions" → Import Questions modal
  // POST /api/questions/import (multipart/form-data)
  const uploadFile = async (file, metadata = {}) => {
    try {
      const email = getEmail();
      const formData = new FormData();
      if (file) formData.append("file", file);
      if (metadata.testName) formData.append("test_name", metadata.testName);
      if (metadata.noOfQuestions) formData.append("no_of_questions", metadata.noOfQuestions);
      if (metadata.level) formData.append("level", metadata.level);
      if (metadata.exam) formData.append("exam", metadata.exam);
      const cls = metadata.clas || metadata.className || metadata.class;
      if (cls) {
        formData.append("clas", cls);
      }
      if (metadata.chapter || metadata.chapters) {
        const ch = metadata.chapters || metadata.chapter;
        const chVal = Array.isArray(ch) ? ch.join(",") : ch;
        if (chVal) formData.append("chapter", chVal);
      }
      const con = metadata.concepts || metadata.concept || metadata.subTopic || metadata.sub_topic;
      if (con) {
        const conVal = Array.isArray(con) ? con.join(",") : con;
        if (conVal) formData.append("concept", conVal);
      }
      formData.append("email", email);

      // const endpoint = "/api/questions/import";
      const endpoint = metadata.isV2 || metadata.endpoint ? (metadata.endpoint || "/api/questions/import-v2") : "/api/questions/import";
      const { data: uploadRes } = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toggleNotify({
        type: "success",
        title: "Success",
        description: uploadRes?.message || "File upload complete. Background parsing started.",
      });

      return uploadRes;
    } catch (err) {
      console.error("Upload error:", err);
      const primaryDetail = err?.response?.data?.detail;

      // If specific conflict / duplication error from backend, return error detail directly
      if (primaryDetail) {
        toggleNotify({
          type: "error",
          title: "Upload Error",
          description: primaryDetail,
        });
        const customErr = new Error(primaryDetail);
        customErr.response = err.response;
        throw customErr;
      }

      // Fallback endpoint if specific route differs
      try {
        const email = getEmail();
        const formData = new FormData();
        if (file) formData.append("file", file);
        if (metadata.testName) formData.append("test_name", metadata.testName);
        if (metadata.noOfQuestions) formData.append("no_of_questions", metadata.noOfQuestions);
        if (metadata.level) formData.append("level", metadata.level);
        if (metadata.exam) formData.append("exam", metadata.exam);
        const fbCls = metadata.clas || metadata.className || metadata.class;
        if (fbCls) {
          formData.append("clas", fbCls);
        }
        if (metadata.chapter || metadata.chapters) {
        const ch = metadata.chapters || metadata.chapter;
        const chVal = Array.isArray(ch) ? ch.join(",") : ch;
        if (chVal) formData.append("chapter", chVal);
      }
        const fbCon = metadata.concepts || metadata.concept || metadata.subTopic || metadata.sub_topic;
        if (fbCon) {
          const conVal = Array.isArray(fbCon) ? fbCon.join(",") : fbCon;
          if (conVal) formData.append("concept", conVal);
        }
        formData.append("email", email);

        const { data: fallbackRes } = await api.post("/teacher/exam/upload-file", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return fallbackRes;
      } catch (fallbackErr) {
        const finalMsg = primaryDetail || fallbackErr?.response?.data?.detail || err?.message || "Failed to upload file. Please try again.";
        toggleNotify({
          type: "error",
          title: "Error",
          description: finalMsg,
        });
        const customErr = new Error(finalMsg);
        customErr.response = err?.response || fallbackErr?.response;
        throw customErr;
      }
    }
  };

  // Dedicated import-v2 helper (used by Question Bank page)
  const uploadFileV2 = async (file, metadata = {}) => {
    return uploadFile(file, { ...metadata, isV2: true });
  };

  // 3. "Imported Questions" modal (list, checkboxes, Search, Select All, pagination)
  // GET /api/questions/import/{test_name}?email=...&page=1&question_type=...&search=...
  const fetchImportedQuestions = async ({
    testName,
    page = 1,
    questionType = "",
    search = "",
  } = {}) => {
    try {
      const email = getEmail();
      const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
      const validTestName = (cleanName && !cleanName.includes("@"))
        ? cleanName
        : "NEET Biology Mock Test-01";
      const encodedTestName = encodeURIComponent(validTestName);

      const params = new URLSearchParams();
      if (email) params.append("email", email);
      if (page) params.append("page", String(page));
      if (questionType && questionType !== "All Question Types" && questionType !== "all" && questionType !== "All") {
        params.append("question_type", questionType);
      }
      if (search && String(search).trim()) {
        params.append("search", String(search).trim());
      }

      const { data: res } = await api.get(
        `/api/questions/import/${encodedTestName}?${params.toString()}`
      );
      return res;
    } catch (err) {
      console.error("Error fetching imported questions:", err);
      return null;
    }
  };

  // 4. "Add to Question Paper" button (Imported Flow)
  // POST /api/question-paper/{test_name}/questions?email
  // (alias POST /teacher/tests/{test_name}/questions)
  // Body: {"question_ids": ["UQ1", "UQ2", 4531]}
  const addQuestionsToPaper = async ({ testName, questionIds = [] } = {}) => {
    try {
      const email = getEmail();
      const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
      const validTestName = (cleanName && !cleanName.includes("@"))
        ? cleanName
        : "NEET Biology Mock Test-01";
      const encodedTestName = encodeURIComponent(validTestName);

      let res = null;
      try {
        const { data } = await api.post(
          `/api/question-paper/${encodedTestName}/questions?email=${encodeURIComponent(email)}`,
          { question_ids: questionIds }
        );
        res = data;
      } catch (primaryErr) {
        if (primaryErr?.response) {
          throw primaryErr;
        }
        // Try fallback alias endpoint
        const { data } = await api.post(
          `/teacher/tests/${encodedTestName}/questions?email=${encodeURIComponent(email)}`,
          { question_ids: questionIds }
        );
        res = data;
      }

      const added = res?.added ?? res?.added_count ?? 0;
      const skipped = res?.skipped ?? res?.skipped_count ?? 0;
      let notifyDesc = res?.message;
      if (!notifyDesc) {
        if (added > 0) {
          notifyDesc = `Added ${added} question(s) to paper.`;
        } else if (skipped > 0) {
          notifyDesc = `${skipped} question(s) were already added to this paper.`;
        } else {
          notifyDesc = "Question paper updated successfully.";
        }
      }

      toggleNotify({
        type: (res?.status === "success" || added > 0) ? "success" : "info",
        title: added > 0 ? "Added to Question Paper" : "Question Paper Updated",
        description: notifyDesc,
      });
      return res;
    } catch (err) {
      console.error("Error adding questions to paper:", err);
      const errorMsg = extractDetailMessage(err?.response?.data?.detail, err?.message || "Failed to add questions to paper.");
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  // 4b. "Add to Question Paper" button for categorized / AI questions
  // POST /api/question-paper/{test_name}/add-categorized-questions?email={email}
  // Body: { "question_ids": ["UQ1", "UQ2", 4531] }
  const addCategorizedQuestionsToPaper = async ({ testName, questionIds = [] } = {}) => {
    try {
      const email = getEmail();
      const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
      const validTestName = (cleanName && !cleanName.includes("@"))
        ? cleanName
        : "NEET Biology Mock Test-01";
      const encodedTestName = encodeURIComponent(validTestName);

      let res = null;
      try {
        const { data } = await api.post(
          `/api/question-paper/${encodedTestName}/add-categorized-questions?email=${encodeURIComponent(email)}`,
          { question_ids: questionIds }
        );
        res = data;
      } catch (primaryErr) {
        if (primaryErr?.response) {
          throw primaryErr;
        }
        // Try fallback endpoint
        const { data } = await api.post(
          `/api/question-paper/${encodedTestName}/questions?email=${encodeURIComponent(email)}`,
          { question_ids: questionIds }
        );
        res = data;
      }

      const added = res?.added ?? res?.added_count ?? (Array.isArray(questionIds) ? questionIds.length : 0);
      const skipped = res?.skipped ?? res?.skipped_count ?? 0;
      let notifyDesc = res?.message;
      if (!notifyDesc) {
        if (added > 0) {
          notifyDesc = `Added ${added} question(s) to paper.`;
        } else if (skipped > 0) {
          notifyDesc = `${skipped} question(s) were already added to this paper.`;
        } else {
          notifyDesc = "Question paper updated successfully.";
        }
      }

      toggleNotify({
        type: (res?.status === "success" || added > 0) ? "success" : "info",
        title: added > 0 ? "Added to Question Paper" : "Question Paper Updated",
        description: notifyDesc,
      });
      return res;
    } catch (err) {
      console.error("Error adding categorized questions to paper:", err);
      const errorMsg = extractDetailMessage(err?.response?.data?.detail, err?.message || "Failed to add categorized questions to paper.");
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  // 5. Edit a single question (pencil icon)
  // PUT /api/updated-question-bank/questions/{question_id}?email (for AI generated)
  // PUT /api/questions/{question_id}?email (for non-AI / imported)
  const editQuestion = async (questionId, questionData, isAi = false) => {
    try {
      const email = getEmail();
      const basePath = isAi ? "/api/updated-question-bank/questions" : "/api/questions";
      const { data: res } = await api.put(
        `${basePath}/${questionId}?email=${encodeURIComponent(email)}`,
        questionData
      );
      toggleNotify({
        type: "success",
        title: "Question Updated",
        description: res?.message || `Question ${res?.question_id || questionId} updated successfully.`,
      });
      return res;
    } catch (err) {
      console.error("Error editing question:", err);
      const errorMsg = err?.response?.data?.detail || err?.message || "Failed to edit question.";
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  const resolveImageBasePath = (source) => {
    if (source === "ai" || source === true) {
      return "/api/updated-question-bank/questions";
    }
    if (
      source === "bank" ||
      source === "question_bank" ||
      source === "saved" ||
      source === "saved-questions" ||
      source === "saved_questions"
    ) {
      return "/api/saved-questions";
    }
    return "/api/questions";
  };

  const formatSavedQuestionId = (id) => {
    if (id === null || id === undefined || id === "") return "";
    const str = String(id).trim();
    if (str.toUpperCase().startsWith("UQ")) {
      return `UQ${str.replace(/^UQ_?/i, "")}`;
    }
    return `UQ${str}`;
  };

  // Upload/replace question image (stem or option)
  // POST /api/updated-question-bank/questions/{question_id}/image?email={email} (for AI generated)
  // POST /api/saved-questions/{question_id}/image?email={email} (for question bank, requires UQ prefix e.g. UQ6936)
  // POST /api/questions/{question_id}/image?email={email} (for non-AI / imported)
  const uploadQuestionImage = async (questionId, target, file, source = "import") => {
    try {
      const email = getEmail();
      const formData = new FormData();
      formData.append("target", target);
      formData.append("file", file);

      const basePath = resolveImageBasePath(source);
      const apiQuestionId =
        basePath === "/api/saved-questions"
          ? formatSavedQuestionId(questionId)
          : questionId;

      const { data: res } = await api.post(
        `${basePath}/${encodeURIComponent(apiQuestionId)}/image?email=${encodeURIComponent(email)}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toggleNotify({
        type: "success",
        title: "Image Uploaded",
        description: res?.message || `Image uploaded for ${target}.`,
      });
      return res;
    } catch (err) {
      console.error("Error uploading image:", err);
      const errorMsg = err?.response?.data?.detail || err?.message || "Failed to upload image.";
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  // Delete question image (stem or option)
  // DELETE /api/updated-question-bank/questions/{question_id}/image?email={email}&target={target}&url={url} (for AI generated)
  // DELETE /api/saved-questions/{question_id}/image?email={email}&target={target}&url={url} (for question bank, requires UQ prefix e.g. UQ6936)
  // DELETE /api/questions/{question_id}/image?email={email}&target={target}&url={url} (for non-AI / imported)
  const deleteQuestionImage = async (questionId, target, url = "", source = "import") => {
    try {
      const email = getEmail();
      const cleanUrl = typeof url === "string" ? url.trim() : "";
      const params = {
        email,
        target,
      };
      if (cleanUrl && cleanUrl !== "null" && cleanUrl !== "undefined") {
        params.url = cleanUrl;
      }

      const basePath = resolveImageBasePath(source);
      const apiQuestionId =
        basePath === "/api/saved-questions"
          ? formatSavedQuestionId(questionId)
          : questionId;
      const deleteFn = api.del || api.delete;

      let res = null;
      try {
        const { data } = await deleteFn(
          `${basePath}/${encodeURIComponent(apiQuestionId)}/image`,
          { params }
        );
        res = data;
      } catch (primaryErr) {
        console.warn("Primary DELETE with params failed, trying query string fallback:", primaryErr);
        let queryStr = `email=${encodeURIComponent(email)}&target=${encodeURIComponent(target)}`;
        if (cleanUrl && cleanUrl !== "null" && cleanUrl !== "undefined") {
          queryStr += `&url=${encodeURIComponent(cleanUrl)}`;
        }
        const { data } = await deleteFn(
          `${basePath}/${encodeURIComponent(apiQuestionId)}/image?${queryStr}`
        );
        res = data;
      }

      toggleNotify({
        type: "success",
        title: "Image Removed",
        description: res?.message || `Image removed for ${target}.`,
      });
      return res || { status: "success" };
    } catch (err) {
      console.error("Error deleting image:", err);
      const errorMsg = err?.response?.data?.detail || err?.message || "Failed to delete image.";
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  // 6. "Complete Question Paper" button
  // POST /teacher/tests/{test_name}/complete?email
  // (fallback: POST /api/question-paper/{test_name}/complete)
  const completeQuestionPaper = async (testName) => {
    try {
      const email = getEmail();
      const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
      const validTestName = (cleanName && !cleanName.includes("@"))
        ? cleanName
        : "NEET Biology Mock Test-01";
      const encodedTestName = encodeURIComponent(validTestName);

      let res = null;
      try {
        const { data } = await api.post(
          `/teacher/tests/${encodedTestName}/complete?email=${encodeURIComponent(email)}`
        );
        res = data;
      } catch (primaryErr) {
        if (primaryErr?.response) {
          throw primaryErr;
        }
        // Try fallback alias endpoint if network error
        const { data } = await api.post(
          `/api/question-paper/${encodedTestName}/complete?email=${encodeURIComponent(email)}`
        );
        res = data;
      }

      const qCount = res?.no_of_questions ?? res?.total_questions ?? "";
      const testTitle = res?.test_name || validTestName;

      toggleNotify({
        type: "success",
        title: "Question Paper Completed",
        description: res?.message || `Question paper "${testTitle}" completed successfully${qCount ? ` with ${qCount} questions` : ""}.`,
      });
      return res;
    } catch (err) {
      console.error("Error completing question paper:", err);
      const errorMsg = err?.response?.data?.detail || err?.message || "Failed to complete question paper.";
      toggleNotify({
        type: "error",
        title: "Error",
        description: errorMsg,
      });
      return null;
    }
  };

  // 7. "View created question papers" / "Created Test" table
  // GET /teacher/question-papers?email&page=1&page_size=8
  const fetchCreatedQuestionPapers = async ({ page = 1, pageSize = 8, filters = {} } = {}) => {
    try {
      const email = getEmail();
      let url = `/teacher/question-papers?email=${encodeURIComponent(email)}&page=${page}&page_size=${pageSize}`;
      if (filters?.testName) url += `&test_name=${encodeURIComponent(filters.testName)}`;
      if (filters?.testCategory) url += `&test_category=${encodeURIComponent(filters.testCategory)}`;
      if (filters?.exam) url += `&exam=${encodeURIComponent(filters.exam)}`;
      if (filters?.className) url += `&class=${encodeURIComponent(filters.className)}`;
      if (filters?.chapter) url += `&chapter=${encodeURIComponent(filters.chapter)}`;
      if (filters?.subTopic) url += `&sub_topic=${encodeURIComponent(filters.subTopic)}`;

      const { data: res } = await api.get(url);
      return res;
    } catch (err) {
      console.error("Error fetching created question papers:", err);
      return null;
    }
  };

  // 8. Question Bank taxonomy tree (exam → class → subject → chapter → concept counts)
  // GET /api/question-bank/manage?
  const fetchTaxonomyTree = async () => {
    try {
      const email = getEmail();
      const { data: res } = await api.get(
        `/api/question-bank/manage?email=${encodeURIComponent(email)}`
      );
      return res;
    } catch (err) {
      console.error("Error fetching taxonomy tree:", err);
      return null;
    }
  };

  // 9. Fetch questions for a created/single question paper (used when viewing a question paper in Created Test table details)
  // GET /api/question-paper/{test_name}/questions?email=varsha.r.apr3@gmail.com&page=1
  const fetchQuestionPaperQuestions = async ({ testName, page = 1 } = {}) => {
    try {
      const email = getEmail();
      const rawName = (testName || "").trim();
      const validTestName = rawName || "NEET Biology Mock Test-01";
      const encodedTestName = encodeURIComponent(validTestName);
      const { data: res } = await api.get(
        `/api/question-paper/${encodedTestName}/questions?email=${encodeURIComponent(email)}&page=${page}`
      );
      return res;
    } catch (err) {
      console.error("Error fetching question paper questions:", err);
      return null;
    }
  };

  // 10. Fetch teacher active test list (used in upload modal test name dropdown)
  // GET /teacher/test/active-list?email=varsha.r.apr3@gmail.com&role=teacher&page=1&page_size=100
  const fetchTeacherTestList = async ({ page = 1, pageSize = 100 } = {}) => {
    try {
      const email = getEmail();
      let res = null;
      try {
        const { data } = await api.get(
          `/teacher/test/active-list?email=${encodeURIComponent(email)}&role=teacher&page=${page}&page_size=${pageSize}`
        );
        res = data;
      } catch (primaryErr) {
        if (primaryErr?.response) {
          throw primaryErr;
        }
        // Fallback to /teacher/test/list
        const { data } = await api.get(
          `/teacher/test/list?email=${encodeURIComponent(email)}&role=teacher&page=${page}&page_size=${pageSize}`
        );
        res = data;
      }
      return res;
    } catch (err) {
      console.error("Error fetching teacher active test list:", err);
      return null;
    }
  };

  // 11. Fetch teacher exams
  // GET /teacher/test/exams?email=varsha.r.apr3@gmail.com&role=teacher
  const fetchTeacherExams = async () => {
    try {
      const email = getEmail();
      const { data: res } = await api.get(
        `/teacher/test/exams?email=${encodeURIComponent(email)}&role=teacher`
      );
      return res;
    } catch (err) {
      console.error("Error fetching teacher exams:", err);
      return null;
    }
  };

  // 12. Fetch teacher chapters
  // GET /teacher/test/chapters?exam=JEE&class=Class%2011&email=varsha.r.apr3@gmail.com&role=teacher
  const fetchTeacherChapters = async ({ exam = "JEE", className = "Class 11" } = {}) => {
    try {
      const email = getEmail();
      const { data: res } = await api.get(
        `/teacher/test/chapters?exam=${encodeURIComponent(exam)}&class=${encodeURIComponent(className)}&email=${encodeURIComponent(email)}&role=teacher`
      );
      return res;
    } catch (err) {
      console.error("Error fetching teacher chapters:", err);
      return null;
    }
  };

  // 13. Fetch teacher sub topics
  // GET /teacher/test/sub-topics?exam=JEE&chapter=Chapter%3A1-Basic%20Concepts%20of%20Chemistry&email=varsha.r.apr3@gmail.com&role=teacher
  const fetchTeacherSubTopics = async ({ exam = "JEE", chapter = "" } = {}) => {
    try {
      const email = getEmail();
      if (!chapter) return null;
      const { data: res } = await api.get(
        `/teacher/test/sub-topics?exam=${encodeURIComponent(exam)}&chapter=${encodeURIComponent(chapter)}&email=${encodeURIComponent(email)}&role=teacher`
      );
      return res;
    } catch (err) {
      console.error("Error fetching teacher sub topics:", err);
      return null;
    }
  };

  // 14. Search MCQ Categorized Questions
  // GET /api/mcq_categorised_updated-search?email=...&exam=NEET&class=Class%2011&subject=Chemistry&chapter=...&limit=100&offset=0
  const searchCategorizedQuestions = async ({
    email = getEmail(),
    exam = "NEET",
    className = "",
    subject = "",
    chapter = "",
    concept = "all",
    questionType = "all",
    search = "",
    limit = 10,
    offset = 0,
  } = {}) => {
    try {
      const userMail = email || getEmail();
      const queryParams = [];
      if (userMail) queryParams.push(`email=${encodeURIComponent(userMail)}`);
      if (exam) queryParams.push(`exam=${encodeURIComponent(exam)}`);
      if (className) queryParams.push(`class=${encodeURIComponent(className)}`);
      if (subject) queryParams.push(`subject=${encodeURIComponent(subject)}`);
      if (chapter) queryParams.push(`chapter=${encodeURIComponent(chapter)}`);

      const conceptVal = (concept && String(concept).trim()) ? String(concept).trim() : "all";
      queryParams.push(`concept=${encodeURIComponent(conceptVal)}`);

      const typeVal = (questionType && String(questionType).trim()) ? String(questionType).trim() : "all";
      queryParams.push(`question_type=${encodeURIComponent(typeVal)}`);

      if (search && String(search).trim()) {
        queryParams.push(`search=${encodeURIComponent(String(search).trim())}`);
      }

      if (limit) queryParams.push(`limit=${encodeURIComponent(limit)}`);
      queryParams.push(`offset=${encodeURIComponent(offset)}`);

      const queryString = queryParams.join("&");
      let res = null;
      try {
        const { data } = await api.get(`/api/mcq_categorised_updated-search-unique?${queryString}`);
        res = data;
      } catch (primaryErr) {
        console.error("Error with /api/mcq_categorised_updated-search-unique:", primaryErr);
        try {
          const { data: fallbackData } = await api.get(`/api/mcq-categorized/search?${queryString}`);
          res = fallbackData;
        } catch (fallbackErr) {
          throw primaryErr;
        }
      }
      return res;
    } catch (err) {
      console.error("Error searching categorized questions:", err);
      return null;
    }
  };

  // 15. Fetch MCQ Filter Question Types
  // GET /api/questions/imported-unique-for-all/filters?email=... or /api/updated-question-bank/filters/question_type
  const fetchMcqQuestionTypes = async () => {
    try {
      const email = getEmail();
      if (email) {
        try {
          const { data: bankRes } = await api.get(
            `/api/questions/imported-unique-for-all/filters?email=${encodeURIComponent(email)}`
          );
          const dataObj = bankRes?.data || bankRes || {};
          const rawTypes = Array.isArray(dataObj.question_type)
            ? dataObj.question_type
            : Array.isArray(dataObj?.data?.question_type)
            ? dataObj.data.question_type
            : [];
          if (Array.isArray(rawTypes) && rawTypes.length > 0) return rawTypes;
        } catch (filtersErr) {
          // continue to next endpoint
        }
      }
      const { data: res } = await api.get("/api/updated-question-bank/filters/question_type");
      return res;
    } catch (err) {
      console.error("Error fetching question types from /api/updated-question-bank/filters/question_type:", err);
      try {
        const { data: fallbackRes } = await api.get("/api/mcq-categorized/filters/question_type");
        return fallbackRes;
      } catch (fallbackErr) {
        return null;
      }
    }
  };

  // 16. Fetch MCQ Filter Concepts
  // GET /api/updated-question-bank/filters/concept?email=...&exam=...&class=...&subject=...&chapter=...
  const fetchMcqConcepts = async ({
    email = getEmail(),
    exam = "NEET",
    className = "Class 11",
    subject = "Chemistry",
    chapter = "",
  } = {}) => {
    try {
      const userMail = email || getEmail();
      const params = new URLSearchParams();
      if (userMail) params.append("email", userMail);
      if (exam) params.append("exam", exam);
      if (className) params.append("class", className);
      if (subject) params.append("subject", subject);
      if (chapter) params.append("chapter", chapter);

      let res = null;
      try {
        const { data } = await api.get(`/api/updated-question-bank/filters/concept?${params.toString()}`);
        res = data;
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.concepts)
            ? res.concepts
            : Array.isArray(res?.concept)
              ? res.concept
              : Array.isArray(res?.data)
                ? res.data
                : [];
        if (list.length > 0) return res;
      } catch (primaryErr) {
        console.warn("Error with /api/updated-question-bank/filters/concept:", primaryErr);
      }

      // Fallback 1: teacher test sub-topics API
      if (chapter) {
        try {
          const { data: subTopicsData } = await api.get(apiTestSubTopics(exam, chapter, userMail, "teacher"));
          if (subTopicsData) {
            const list = subTopicsData.sub_topics || subTopicsData.data || (Array.isArray(subTopicsData) ? subTopicsData : []);
            if (Array.isArray(list) && list.length > 0) {
              return subTopicsData;
            }
          }
        } catch (subTopicErr) {
          console.warn("Error with /teacher/test/sub-topics:", subTopicErr);
        }
      }

      // Fallback 2: mcq-categorized filters concept
      try {
        const { data: fallbackData } = await api.get(`/api/mcq-categorized/filters/concept?${params.toString()}`);
        res = fallbackData;
      } catch (fallbackErr) {
        // fallback failed
      }
      return res;
    } catch (err) {
      console.error("Error fetching MCQ concepts:", err);
      return null;
    }
  };

  return {
    fetchQuestionBank,
    fetchImportedUniqueQuestions,
    uploadFile,
    uploadFileV2,
    fetchImportedQuestions,
    addQuestionsToPaper,
    addCategorizedQuestionsToPaper,
    editQuestion,
    uploadQuestionImage,
    deleteQuestionImage,
    completeQuestionPaper,
    fetchCreatedQuestionPapers,
    fetchTaxonomyTree,
    fetchQuestionPaperQuestions,
    fetchTeacherTestList,
    fetchTeacherExams,
    fetchTeacherChapters,
    fetchTeacherSubTopics,
    searchCategorizedQuestions,
    fetchMcqQuestionTypes,
    fetchMcqConcepts,
  };
};
